import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Briefcase, CheckCircle, Calendar, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicBookingForm() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [dba, setDba] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/public/book/${slug}`)
      .then(({ data }) => {
        if (data.canonical_slug && data.canonical_slug !== slug) {
          navigate(`/book/${data.canonical_slug}`, { replace: true });
        }
        setDba(data);
      })
      .catch(() => setError('Business not found'))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  useEffect(() => {
    if (!selectedDate || !slug) return;
    setLoadingSlots(true);
    setSelectedTime('');
    axios.get(`${API}/api/public/book/${slug}?date=${selectedDate}`)
      .then(({ data }) => {
        if (data.canonical_slug && data.canonical_slug !== slug) {
          navigate(`/book/${data.canonical_slug}`, { replace: true });
        }
        setAvailableSlots(data.available_slots || []);
      })
      .catch(() => setAvailableSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate, slug, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) { setError('Please select a date and time'); return; }
    setSubmitting(true); setError('');
    const f = new FormData(e.target);
    try {
      await axios.post(`${API}/api/public/book/${slug}`, {
        name: f.get('name'), email: f.get('email'), phone: f.get('phone'),
        address: f.get('address'), service_needed: f.get('service_needed'),
        message: f.get('message'), date: selectedDate, time: selectedTime,
        website: f.get('website') || '',
      });
      setSubmitted(true);
    } catch (err) { setError(err.response?.data?.detail || 'Something went wrong'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-zinc-100 flex items-center justify-center"><div className="animate-pulse w-full max-w-md h-96 bg-zinc-200 rounded-lg" /></div>;
  if (error && !dba) return <div className="min-h-screen bg-zinc-100 flex items-center justify-center"><p className="text-zinc-500">{error}</p></div>;

  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-8 h-8 text-emerald-600" /></div>
          <h2 className="text-xl font-bold font-heading" data-testid="booking-success">Booking Submitted!</h2>
          <p className="text-sm text-zinc-500 mt-2">We'll confirm your appointment shortly.</p>
        </div>
      </div>
    );
  }

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4"
      style={{ backgroundImage: `url(https://images.unsplash.com/photo-1694702730868-a34da58c1427?w=1920&q=40)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div className="absolute inset-0 bg-zinc-950/60" />
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white rounded-lg border border-zinc-200 shadow-xl p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-md mb-3" style={{ backgroundColor: dba?.color || '#09090b' }}>
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold font-heading" data-testid="booking-title">{dba?.name}</h1>
            <p className="text-sm text-zinc-500 mt-1">{dba?.tagline || 'Book an appointment'}</p>
            {dba?.business_hours && <p className="text-xs text-zinc-400">{dba.business_hours}</p>}
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 p-2 rounded mb-4">{error}</p>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input name="website" tabIndex={-1} autoComplete="off" className="hidden" data-testid="booking-website-honeypot" />
            <div><Label>Your Name *</Label><Input name="name" required data-testid="booking-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input name="email" type="email" data-testid="booking-email" /></div>
              <div><Label>Phone *</Label><Input name="phone" required data-testid="booking-phone" /></div>
            </div>
            <div><Label>Address</Label><Input name="address" data-testid="booking-address" /></div>
            {dba?.service_categories?.length > 0 && (
              <div><Label>Service *</Label>
                <select name="service_needed" required className="w-full h-9 rounded-md border border-zinc-200 bg-white px-3 text-sm" data-testid="booking-service">
                  <option value="">Select...</option>
                  {dba.service_categories.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}

            {/* Date Selection */}
            <div>
              <Label className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Preferred Date *</Label>
              <Input type="date" min={today} value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} required data-testid="booking-date" />
            </div>

            {/* Time Slot Selection */}
            {selectedDate && (
              <div>
                <Label className="flex items-center gap-1 mb-1.5"><Clock className="w-3.5 h-3.5" /> Available Times</Label>
                {loadingSlots ? (
                  <div className="flex gap-2">{[1,2,3].map(i => <div key={i} className="h-9 w-16 bg-zinc-200 rounded animate-pulse" />)}</div>
                ) : availableSlots.length === 0 ? (
                  <p className="text-sm text-amber-600">No slots available on this date. Try another day.</p>
                ) : (
                  <div className="grid grid-cols-5 gap-1.5" data-testid="booking-time-slots">
                    {availableSlots.map(slot => (
                      <button key={slot} type="button" onClick={() => setSelectedTime(slot)}
                        className={`py-2 rounded text-xs font-medium border transition-all ${selectedTime === slot ? 'bg-zinc-950 text-white border-zinc-950' : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400'}`}
                        data-testid={`booking-slot-${slot}`}>{slot}</button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div><Label>Notes</Label><Textarea name="message" rows={2} placeholder="Anything we should know..." data-testid="booking-message" /></div>
            <Button type="submit" className="w-full" disabled={submitting || !selectedTime} data-testid="booking-submit">
              {submitting ? 'Booking...' : 'Book Appointment'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
